/**
 * 在途监管平台 — 通用组件
 */
import type { ReactNode } from 'react';
import { alertLevelTone, alertTypeText, transitStatusText } from '@/utils/format';

/** 状态标签（运输单/检疫/档案等通用） */
export function StatusTag({ tone, children }: { tone: 'primary' | 'green' | 'blue' | 'amber' | 'red' | 'muted'; children: ReactNode }) {
  const bg: Record<string, string> = {
    primary: 'var(--color-primary-soft)',
    green: 'var(--color-green-soft)',
    blue: 'var(--color-blue-soft)',
    amber: 'var(--color-amber-soft)',
    red: 'var(--color-red-soft)',
    muted: 'var(--color-bg-soft)',
  };
  const fg: Record<string, string> = {
    primary: 'var(--color-primary)',
    green: 'var(--color-green-dark)',
    blue: 'var(--color-blue)',
    amber: 'var(--color-amber-dark)',
    red: 'var(--color-red)',
    muted: 'var(--color-ink-secondary)',
  };
  return (
    <span className="tag" style={{ background: bg[tone], color: fg[tone] }}>
      {children}
    </span>
  );
}

/** 运输单状态标签 */
export function OrderStatusTag({ status }: { status: number }) {
  const toneMap: Record<number, 'primary' | 'green' | 'blue' | 'amber' | 'red' | 'muted'> = {
    0: 'primary',
    1: 'blue',
    2: 'amber',
    3: 'green',
    4: 'green',
    5: 'muted',
  };
  return <StatusTag tone={toneMap[status] ?? 'muted'}>{transitStatusText(status)}</StatusTag>;
}

/** 告警级别标签 */
export function AlertLevelTag({ level }: { level: number }) {
  const tone = alertLevelTone(level);
  const label = level === 1 ? '黄色' : level === 2 ? '橙色' : level === 3 ? '红色' : '—';
  return <StatusTag tone={tone === 'muted' ? 'muted' : (tone as 'amber' | 'red')}>{label}</StatusTag>;
}

/** 告警类型标签 */
export function AlertTypeTag({ type }: { type: number }) {
  return <StatusTag tone="muted">{alertTypeText(type)}</StatusTag>;
}

/** 统计卡片 */
export function StatCard({
  label,
  value,
  tone = 'blue',
  icon,
}: {
  label: string;
  value: string | number;
  tone?: 'blue' | 'green' | 'amber' | 'red' | 'primary';
  icon?: string;
}) {
  const colorMap: Record<string, string> = {
    blue: 'var(--color-blue)',
    green: 'var(--color-green)',
    amber: 'var(--color-amber)',
    red: 'var(--color-red)',
    primary: 'var(--color-primary)',
  };
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {icon && (
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              background: colorMap[tone] + '1a',
              color: colorMap[tone],
            }}
          >
            {icon}
          </div>
        )}
        <div>
          <div style={{ fontSize: 13, color: 'var(--color-ink-secondary)' }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: colorMap[tone], lineHeight: 1.2 }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

/** 空态 */
export function Empty({ text = '暂无数据' }: { text?: string }) {
  return (
    <div className="empty">
      <div className="empty-icon">📭</div>
      <div>{text}</div>
    </div>
  );
}

/** 加载态 */
export function Loading({ text = '加载中…' }: { text?: string }) {
  return (
    <div className="empty">
      <div className="empty-icon">⏳</div>
      <div>{text}</div>
    </div>
  );
}

/** 页面头部 */
export function PageHeader({ title, sub, extra }: { title: string; sub?: string; extra?: ReactNode }) {
  return (
    <div className="row-between" style={{ marginBottom: 20 }}>
      <div>
        <div className="page-title">{title}</div>
        {sub && <div className="page-sub" style={{ marginBottom: 0 }}>{sub}</div>}
      </div>
      {extra}
    </div>
  );
}

/** 哈希展示 */
export function HashCell({ value, title }: { value?: string; title?: string }) {
  if (!value) return <span style={{ color: 'var(--color-ink-muted)' }}>—</span>;
  return (
    <span
      title={title ?? value}
      style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-blue)', wordBreak: 'break-all' }}
    >
      {value.length > 24 ? `${value.slice(0, 12)}…${value.slice(-8)}` : value}
    </span>
  );
}
